import { Cartridge, IMapper, IMapperType, MirrorMode } from '@/Emulator';
import { StaticImplements }                            from '@/LanguageUtilities';

/**
 * Mapper ID 4, also known as MMC3.
 *
 * The Nintendo MMC3 is a mapper ASIC used in Nintendo's TxROM Game Pak boards.
 * Most common TxROM boards, along with the NES-HKROM board (which uses the
 * Nintendo MMC6), are assigned to iNES Mapper 004.
 */
@StaticImplements<IMapperType>()
export class Mapper004 implements IMapper
{
    // --- MMC3 Bank Switching State ---
    private targetRegister: number = 0;
    private prgBankMode: number    = 0;
    private chrInversion: number   = 0;

    private readonly registers: Uint32Array  = new Uint32Array(8).fill(0);
    private readonly prgOffsets: Uint32Array = new Uint32Array(4).fill(0);
    private readonly chrOffsets: Uint32Array = new Uint32Array(8).fill(0);

    private prgBankCount: number = 0;
    private chrBankCount: number = 0;
    private _currentMirrorMode: MirrorMode;

    private irqPending: boolean       = false;
    private irqEnabled: boolean       = false;
    private irqReloadPending: boolean = false;
    private irqCounter: number        = 0;
    private irqLatch: number          = 0;
    private lastA12: number           = 0;
    private a12LowCount: number       = 0;

    /**
     * @see {@link IMapperType.supports}
     */
    public static supports(cartridge: Cartridge): boolean
    {
        return cartridge.mapperId === 4;
    }

    private prgBankOffset: number = 0;
    private chrBankOffset: number = 0;

    constructor(private readonly cartridge: Cartridge)
    {
        this.prgBankCount = Math.max(1, Math.floor(this.cartridge.prgRom.length / 8192));
        this.chrBankCount = Math.max(1, Math.floor(this.cartridge.chrData.length / 1024));

        this._currentMirrorMode = this.cartridge.mirrorMode;
        this.updateOffsets();
    }

    /**
     * @inheritDoc
     */
    public get mirrorMode(): MirrorMode
    {
        return this._currentMirrorMode;
    }

    // region === CPU ===

    /**
     * @inheritDoc
     */
    public peekCpu(address: number): number
    {
        return 0;
    }

    /**
     * @inheritDoc
     */
    public readCpu(address: number): number
    {
        if (address >= 0x6000 && address <= 0x7FFF) {
            return this.cartridge.prgRam[address - 0x6000];
        }

        if (address >= 0x8000 && address <= 0xFFFF) {
            const slotIndex: number = (address - 0x8000) >> 13; // Divide by 8KB
            const offset: number    = address & 0x1FFF;         // Remainder inside the 8KB

            return this.cartridge.prgRom[this.prgOffsets[slotIndex] + offset];
        }
        return 0;
    }

    /**
     * @inheritDoc
     */
    public writeCpu(address: number, value: number): void
    {
        if (address >= 0x6000 && address <= 0x7FFF) {
            this.cartridge.prgRam[address - 0x6000] = value;
            return;
        }

        if (address >= 0x8000 && address <= 0xFFFF) {
            // MMC3 uses the lowest bit to differentiate registers
            const isEven: boolean = (address & 1) === 0;

            if (address >= 0x8000 && address <= 0x9FFF) {
                if (isEven) {
                    // $8000: Bank Select
                    this.targetRegister = value & 0x07;
                    this.prgBankMode    = (value >> 6) & 1;
                    this.chrInversion   = (value >> 7) & 1;
                    this.updateOffsets();
                } else {
                    // $8001: Bank Data
                    this.registers[this.targetRegister] = value;
                    this.updateOffsets();
                }
            } else if (address >= 0xA000 && address <= 0xBFFF) {
                if (isEven) {
                    // $A000: Mirroring Setup
                    this._currentMirrorMode = (value & 1) === 0 ? MirrorMode.Vertical : MirrorMode.Horizontal;
                } else {
                    // $A001: PRG RAM Protect (Optional for basic MMC3)
                }
            } else if (address >= 0xC000 && address <= 0xDFFF) {
                if (isEven) {
                    // $C000: IRQ Latch (Update the reload value)
                    this.irqLatch = value;
                } else {
                    // $C001: IRQ Reload (Force the counter to reset next clock)
                    this.irqReloadPending = true;
                }
            } else if (address >= 0xE000 && address <= 0xFFFF) {
                if (isEven) {
                    // $E000: IRQ Disable & Acknowledge
                    this.irqEnabled = false;
                    this.irqPending = false;
                } else {
                    // $E001: IRQ Enable
                    this.irqEnabled = true;
                }
            } else {
                // $8000 - $BFFF: PRG and CHR Bank Switching commands go here
            }
        }
    }

    // endregion

    // region === PPU ===

    /**
     * @inheritDoc
     */
    public peekPpu(address: number): number
    {
        return 0;
    }

    /**
     * @inheritDoc
     */
    public readPpu(address: number): number
    {
        this.trackA12RisingEdge(address);

        if (address >= 0x0000 && address <= 0x1FFF) {
            const slotIndex: number = address >> 10;    // Divide by 1KB
            const offset: number    = address & 0x03FF; // Remainder inside the 1KB

            return this.cartridge.chrData[this.chrOffsets[slotIndex] + offset];
        }

        return 0;
    }

    /**
     * @inheritDoc
     */
    public writePpu(address: number, value: number): void
    {
        if (this.cartridge.hasChrRam && address >= 0x0000 && address <= 0x1FFF) {
            this.cartridge.chrData[this.chrBankOffset + address] = value & 0xFF;
        }
    }

    // endregion

    // region === MMC3 IRQ Tracking ===

    private trackA12RisingEdge(address: number): void
    {
        const a12: number = (address >> 12) & 1;

        if (a12 === 1 && this.lastA12 === 0) {
            if (this.a12LowCount >= 3) {
                this.clockIrq();
            }
            this.a12LowCount = 0;
        } else if (a12 === 0) {
            this.a12LowCount++;
        }

        this.lastA12 = a12;
    }

    private clockIrq(): void
    {
        if (this.irqCounter === 0 || this.irqReloadPending) {
            this.irqCounter       = this.irqLatch;
            this.irqReloadPending = false;
        } else {
            this.irqCounter--;
        }

        if (this.irqCounter === 0 && this.irqEnabled) {
            this.irqPending = true;
        }
    }

    public get hasPendingInterruptRequest(): boolean
    {
        return this.irqPending;
    }

    // endregion

    private updateOffsets(): void
    {
        const r0 = this.registers[0] % this.chrBankCount;
        const r1 = this.registers[1] % this.chrBankCount;
        const r2 = this.registers[2] % this.chrBankCount;
        const r3 = this.registers[3] % this.chrBankCount;
        const r4 = this.registers[4] % this.chrBankCount;
        const r5 = this.registers[5] % this.chrBankCount;
        const r6 = this.registers[6] % this.prgBankCount;
        const r7 = this.registers[7] % this.prgBankCount;

        // --- CHR Offsets ---
        if (this.chrInversion === 0) {
            // Mode 0: 2KB banks at $0000-$0FFF, 1KB banks at $1000-$1FFF
            this.chrOffsets[0] = (r0 & 0xFE) * 1024;
            this.chrOffsets[1] = ((r0 & 0xFE) + 1) * 1024;
            this.chrOffsets[2] = (r1 & 0xFE) * 1024;
            this.chrOffsets[3] = ((r1 & 0xFE) + 1) * 1024;
            this.chrOffsets[4] = r2 * 1024;
            this.chrOffsets[5] = r3 * 1024;
            this.chrOffsets[6] = r4 * 1024;
            this.chrOffsets[7] = r5 * 1024;
        } else {
            // Mode 1: 1KB banks at $0000-$0FFF, 2KB banks at $1000-$1FFF
            this.chrOffsets[0] = r2 * 1024;
            this.chrOffsets[1] = r3 * 1024;
            this.chrOffsets[2] = r4 * 1024;
            this.chrOffsets[3] = r5 * 1024;
            this.chrOffsets[4] = (r0 & 0xFE) * 1024;
            this.chrOffsets[5] = ((r0 & 0xFE) + 1) * 1024;
            this.chrOffsets[6] = (r1 & 0xFE) * 1024;
            this.chrOffsets[7] = ((r1 & 0xFE) + 1) * 1024;
        }

        // --- PRG Offsets ---
        const prgLast: number       = (this.prgBankCount - 1) * 8192;
        const prgSecondLast: number = (this.prgBankCount - 2) * 8192;

        if (this.prgBankMode === 0) {
            // Mode 0: $8000 is R6, $C000 is fixed to second-to-last bank
            this.prgOffsets[0] = r6 * 8192;
            this.prgOffsets[1] = r7 * 8192;
            this.prgOffsets[2] = prgSecondLast;
            this.prgOffsets[3] = prgLast;
        } else {
            // Mode 1: $C000 is R6, $8000 is fixed to second-to-last bank
            this.prgOffsets[0] = prgSecondLast;
            this.prgOffsets[1] = r7 * 8192;
            this.prgOffsets[2] = r6 * 8192;
            this.prgOffsets[3] = prgLast;
        }
    }

    /**
     * @inheritDoc
     */
    public serialize(): Record<string, any>
    {
        return {
            targetRegister:   this.targetRegister,
            prgBankMode:      this.prgBankMode,
            chrInversion:     this.chrInversion,
            registers:        Array.from(this.registers), // Convert to standard array for JSON
            irqPending:       this.irqPending,
            irqEnabled:       this.irqEnabled,
            irqReloadPending: this.irqReloadPending,
            irqCounter:       this.irqCounter,
            irqLatch:         this.irqLatch,
            lastA12:          this.lastA12,
            a12LowCount:      this.a12LowCount,
        };
    }

    /**
     * @inheritDoc
     */
    public deserialize(state: Record<string, any>): void
    {
        this.registers.set(state.registers);

        this.targetRegister   = state.targetRegister;
        this.prgBankMode      = state.prgBankMode;
        this.chrInversion     = state.chrInversion;
        this.irqPending       = state.irqPending;
        this.irqEnabled       = state.irqEnabled;
        this.irqReloadPending = state.irqReloadPending;
        this.irqCounter       = state.irqCounter;
        this.irqLatch         = state.irqLatch;
        this.lastA12          = state.lastA12;
        this.a12LowCount      = state.a12LowCount;

        // Recompute the offsets based on the restored registers.
        this.updateOffsets();
    }
}
